import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file."
  );
}

/**
 * Every Supabase request gets a hard timeout.
 *
 * Without this, a request to an unreachable backend (paused free-tier project,
 * black-holed network) can hang indefinitely rather than rejecting — the
 * promise never settles, so callers that await it never resume. That is what
 * left the app stuck on its loading screen: the awaited data load simply never
 * finished, so setLoading(false) was never reached.
 *
 * A rejection is always recoverable; a hang never is.
 */
const REQUEST_TIMEOUT_MS = 15_000;

function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  // Respect an caller-supplied signal as well as our timeout.
  if (options.signal) {
    options.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timer)
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: { fetch: fetchWithTimeout },
});
